import { AxiosResponse } from 'axios';
import { ACCOUNT_ORIGIN, ACCOUNT_TWO_FA_PATH } from '@model/const/account';

const STORAGE_KEY_PREFIX = 'acronis_trial_form_2fa:';
const STATE_TTL_MS = 6 * 60 * 60 * 1000;
const HASH_PATTERN = /^#2fa-(return|cancel)-([A-Za-z0-9]+)$/;

export interface TwoFaContext {
    activeForm: string;
    formStep: number | null;
    userData: Record<string, unknown>;
}

interface StoredEntry extends TwoFaContext {
    ts: number;
    preAuthToken: string;
}

function stripSensitive(userData: Record<string, unknown>): Record<string, unknown> {
    const sensitive = ['password', '_recaptchaResponse'];
    return Object.fromEntries(
        Object.entries(userData || {}).filter(([key]) => !sensitive.includes(key)),
    );
}

function saveFlow(flowId: string, entry: StoredEntry): void {
    try {
        window.localStorage.setItem(STORAGE_KEY_PREFIX + flowId, JSON.stringify(entry));
    } catch (e) { /* quota / private mode — flow won't restore on return, best we can do */ }
}

function restoreFlow(flowId: string): StoredEntry | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_PREFIX + flowId);
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

function clearFlow(flowId: string): void {
    try {
        window.localStorage.removeItem(STORAGE_KEY_PREFIX + flowId);
    } catch (e) { /* ignore */ }
}

export function cleanupExpired(): void {
    try {
        const now = Date.now();
        Object.keys(window.localStorage)
            .filter((key) => key.startsWith(STORAGE_KEY_PREFIX))
            .map((key) => key.slice(STORAGE_KEY_PREFIX.length))
            .forEach((flowId) => {
                const entry = restoreFlow(flowId);
                if (!entry || typeof entry.ts !== 'number' || now - entry.ts > STATE_TTL_MS) {
                    clearFlow(flowId);
                }
            });
    } catch (e) { /* ignore */ }
}

function clearHash(): void {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

export function requires2FA(response: AxiosResponse | null | undefined): boolean {
    if (response?.status !== 202) return false;
    if (!response.data?.pre_auth_token) return false;
    return ['pending_setup', 'enabled'].includes(response.data.two_fa_status);
}

// Lock is rate-limit based (HTTP 429 from account). Cooldown is fixed at 1 minute,
// so the response body / `x-ratelimit-reset` header carry no extra value.
export function isOtpLocked(response: AxiosResponse | null | undefined): boolean {
    return response?.status === 429;
}

// Until SDT-11744 ships, Account can't run first-time 2FA setup through the handoff,
// so pending_setup users must finish it on account.acronis.com first. Remove after SDT-11744.
export function requires2FASetupFirst(response: AxiosResponse | null | undefined): boolean {
    return response?.status === 202 && response.data?.two_fa_status === 'pending_setup';
}

export function setupFirstMessage(override?: string): string {
    return override || 'Please finish setting up two-factor authentication at ' +
        `<a href="${ACCOUNT_ORIGIN}" target="_blank">account.acronis.com</a>, then log in here again.`;
}

export function redirectTo2FA(response: AxiosResponse, context: TwoFaContext): void {
    const preAuthToken = response?.data?.pre_auth_token;
    if (!preAuthToken) return;

    const flowId = crypto.randomUUID().slice(0, 8);
    saveFlow(flowId, {
        ...context,
        userData: stripSensitive(context.userData || {}),
        ts: Date.now(),
        preAuthToken,
    });

    const base = window.location.origin + window.location.pathname + window.location.search;
    const returnUrl = `${base}#2fa-return-${flowId}`;
    const cancelUrl = `${base}#2fa-cancel-${flowId}`;

    const target = `${ACCOUNT_ORIGIN}${ACCOUNT_TWO_FA_PATH}` +
        `?pre_auth_token=${encodeURIComponent(preAuthToken)}` +
        `&two_fa_status=${encodeURIComponent(response.data.two_fa_status)}` +
        `&redirect_url=${encodeURIComponent(returnUrl)}` +
        `&cancel_url=${encodeURIComponent(cancelUrl)}`;

    window.location.assign(target);
}

export function consumeReturn() {
    const match = HASH_PATTERN.exec(window.location.hash);
    if (!match) return null;

    clearHash();

    const outcome = match[1] as 'return' | 'cancel';
    const flowId = match[2];

    const entry = restoreFlow(flowId);
    clearFlow(flowId);

    if (!entry) return null;

    const context: TwoFaContext = {
        activeForm: entry.activeForm,
        formStep: entry.formStep,
        userData: entry.userData || {},
    };

    return { outcome, context };
}
